import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useTheme,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
} from '@mui/material';
import {
  ArrowBackOutlined,
  EditOutlined,
  EmailOutlined,
  PhoneOutlined,
  LocationOnOutlined,
  BusinessOutlined,
  DescriptionOutlined,
  ReceiptLongOutlined,
  TrendingUpOutlined,
  CheckCircleOutlined,
  PendingOutlined,
  CancelOutlined,
  AddOutlined,
  WorkOutlineOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import { useClientStore } from '../stores/useClientStore';
import { useQuoteStore } from '../stores/useQuoteStore';
import { useFactureStore } from '../stores/useFactureStore';
import { useProjetStore } from '../stores/useProjetStore';
import { Client, Devis, Facture, StatutDevis, StatutFacture, Projet, StatutProjet } from '../types';
import AddressAutocomplete from '../components/AddressAutocomplete';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ClientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { clients, loadClients } = useClientStore();
  const { devis, loadDevis } = useQuoteStore();
  const { factures, loadFactures } = useFactureStore();
  const { projets, loadProjets } = useProjetStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [clientDevis, setClientDevis] = useState<Devis[]>([]);
  const [clientFactures, setClientFactures] = useState<Facture[]>([]);
  const [clientProjets, setClientProjets] = useState<Projet[]>([]);
  const [tabValue, setTabValue] = useState(0);

  // Project dialog state
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectAddress, setProjectAddress] = useState('');
  const [projectCodePostal, setProjectCodePostal] = useState('');
  const [projectVille, setProjectVille] = useState('');

  // Charger les données et trouver le client
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Charger les données si nécessaire (une seule fois)
        if (clients.length === 0) await loadClients();
        if (devis.length === 0) await loadDevis();
        if (factures.length === 0) await loadFactures();
        if (projets.length === 0) await loadProjets();
      } catch (err) {
        console.error('Erreur lors du chargement des données:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };

    loadData();
    // Exécuter une seule fois au montage du composant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mettre à jour les données du client quand les stores changent
  useEffect(() => {
    // Ne pas traiter si les clients ne sont pas encore chargés
    if (clients.length === 0) return;

    // Trouver le client
    const foundClient = clients.find((c) => c.id === Number(id));
    if (!foundClient) {
      // Seulement afficher l'erreur si les clients sont chargés et le client n'existe vraiment pas
      setError('Client non trouvé');
      setClient(null);
      setLoading(false);
      return;
    }

    setClient(foundClient);
    setError(null);

    // Filtrer les projets, devis et factures du client
    const clientProjetsList = projets.filter((p) => p.clientId === Number(id));
    const clientDevisList = devis.filter((d) => d.clientId === Number(id));
    const clientFacturesList = factures.filter((f) => f.clientId === Number(id));

    setClientProjets(clientProjetsList);
    setClientDevis(clientDevisList);
    setClientFactures(clientFacturesList);

    // Terminer le chargement seulement quand le client est trouvé
    setLoading(false);
  }, [id, clients, devis, factures, projets]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Statistiques
  const stats = {
    totalProjets: clientProjets.length,
    projetsActifs: clientProjets.filter((p) => p.statut === StatutProjet.EN_COURS).length,
    projetsTermines: clientProjets.filter((p) => p.statut === StatutProjet.TERMINE).length,
    totalDevis: clientDevis.length,
    devisAcceptes: clientDevis.filter((d) => d.statut === StatutDevis.ACCEPTE).length,
    devisEnvoyes: clientDevis.filter((d) => d.statut === StatutDevis.ENVOYE).length,
    devisBrouillon: clientDevis.filter((d) => d.statut === StatutDevis.BROUILLON).length,
    totalFactures: clientFactures.length,
    facturesPayees: clientFactures.filter((f) => f.statut === StatutFacture.PAYEE).length,
    facturesEnAttente: clientFactures.filter((f) => f.statut === StatutFacture.EN_ATTENTE).length,
    montantTotalDevis: clientDevis.reduce((sum, d) => sum + (d.totalTTC || 0), 0),
    montantTotalFacture: clientFactures.reduce(
      (sum, f) => sum + (f.montantTTC || 0),
      0
    ),
    montantPaye: clientFactures
      .filter((f) => f.statut === StatutFacture.PAYEE)
      .reduce((sum, f) => sum + (f.montantTTC || 0), 0),
    montantEnAttente: clientFactures
      .filter((f) => f.statut === StatutFacture.EN_ATTENTE)
      .reduce((sum, f) => sum + (f.montantTTC || 0), 0),
  };

  const getStatutDevisColor = (statut: StatutDevis) => {
    switch (statut) {
      case StatutDevis.ACCEPTE:
        return 'success';
      case StatutDevis.ENVOYE:
        return 'info';
      case StatutDevis.REFUSE:
        return 'error';
      case StatutDevis.BROUILLON:
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatutDevisLabel = (statut: StatutDevis) => {
    switch (statut) {
      case StatutDevis.ACCEPTE:
        return 'Accepté';
      case StatutDevis.ENVOYE:
        return 'Envoyé';
      case StatutDevis.REFUSE:
        return 'Refusé';
      case StatutDevis.BROUILLON:
        return 'Brouillon';
      default:
        return statut;
    }
  };

  const getStatutFactureColor = (statut: StatutFacture) => {
    switch (statut) {
      case StatutFacture.PAYEE:
        return 'success';
      case StatutFacture.EN_ATTENTE:
        return 'warning';
      case StatutFacture.ANNULEE:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatutFactureLabel = (statut: StatutFacture) => {
    switch (statut) {
      case StatutFacture.PAYEE:
        return 'Payée';
      case StatutFacture.EN_ATTENTE:
        return 'En attente';
      case StatutFacture.ANNULEE:
        return 'Annulée';
      default:
        return statut;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Afficher l'erreur seulement si le chargement est terminé et qu'il y a une erreur ou pas de client
  if (!loading && (error || !client)) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Client non trouvé'}
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBackOutlined />} onClick={() => navigate('/clients')}>
          Retour à la liste
        </Button>
      </Container>
    );
  }

  // Guard supplémentaire pour TypeScript
  if (!client) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/clients')} sx={{ mr: 1 }}>
            <ArrowBackOutlined />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {client.prenom} {client.nom}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Client depuis le {new Date(client.createdAt).toLocaleDateString('fr-FR')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditOutlined />}
          onClick={() => navigate(`/clients/edit/${client.id}`)}
        >
          Modifier
        </Button>
      </Box>

      {/* Informations du client */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Informations du client
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailOutlined sx={{ mr: 2, color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{client.email}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PhoneOutlined sx={{ mr: 2, color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Téléphone
                </Typography>
                <Typography variant="body1">{client.telephone}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOnOutlined sx={{ mr: 2, color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Adresse
                </Typography>
                <Typography variant="body1">
                  {client.adresse}
                  <br />
                  {client.codePostal} {client.ville}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {(client.siren || client.codePaysIso || client.numTvaIntracom) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Informations légales (Factur-X)
              </Typography>
              <Grid container spacing={2}>
                {client.siren && (
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessOutlined sx={{ mr: 2, color: theme.palette.secondary.main }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          SIREN/SIRET
                        </Typography>
                        <Typography variant="body2">{client.siren}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {client.codePaysIso && (
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Code Pays ISO
                      </Typography>
                      <Typography variant="body2">{client.codePaysIso}</Typography>
                    </Box>
                  </Grid>
                )}
                {client.numTvaIntracom && (
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        N° TVA Intracommunautaire
                      </Typography>
                      <Typography variant="body2">{client.numTvaIntracom}</Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Statistiques */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Devis
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.totalDevis}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.devisAcceptes} acceptés
                  </Typography>
                </Box>
                <DescriptionOutlined sx={{ fontSize: 48, color: theme.palette.primary.main, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Factures
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.totalFactures}
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {stats.facturesPayees} payées
                  </Typography>
                </Box>
                <ReceiptLongOutlined sx={{ fontSize: 48, color: theme.palette.secondary.main, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Montant Total Devis
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {stats.montantTotalDevis.toFixed(2)} €
                  </Typography>
                </Box>
                <TrendingUpOutlined sx={{ fontSize: 48, color: theme.palette.info.main, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Montant Payé
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {stats.montantPaye.toFixed(2)} €
                  </Typography>
                  <Typography variant="caption" color="warning.main">
                    {stats.montantEnAttente.toFixed(2)} € en attente
                  </Typography>
                </Box>
                <CheckCircleOutlined sx={{ fontSize: 48, color: theme.palette.success.main, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs pour Projets, Devis et Factures */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="client tabs">
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkOutlineOutlined />
                  <span>Projets ({stats.totalProjets})</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionOutlined />
                  <span>Devis ({stats.totalDevis})</span>
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptLongOutlined />
                  <span>Factures ({stats.totalFactures})</span>
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Tab Projets */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => setProjectDialogOpen(true)}
            >
              Nouveau projet
            </Button>
          </Box>
          {clientProjets.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <WorkOutlineOutlined sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Aucun projet pour ce client
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Cliquez sur "Nouveau projet" pour créer un projet.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {clientProjets.map((projet) => (
                <Grid item xs={12} md={6} key={projet.id}>
                  <Card
                    elevation={2}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => navigate(`/projets/${projet.id}`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {projet.titre}
                        </Typography>
                        <Chip
                          label={projet.statut}
                          size="small"
                          color={
                            projet.statut === StatutProjet.TERMINE
                              ? 'success'
                              : projet.statut === StatutProjet.EN_COURS
                              ? 'warning'
                              : 'default'
                          }
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        📍 {projet.adresseChantier}
                      </Typography>
                      {projet.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {projet.description}
                        </Typography>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          <DescriptionOutlined sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                          {devis.filter((d) => d.projetId === projet.id).length} devis
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <ReceiptLongOutlined sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                          {factures.filter((f) => f.projetId === projet.id).length} factures
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Tab Devis */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={() => navigate(`/devis/new?clientId=${client.id}`)}
            >
              Nouveau devis
            </Button>
          </Box>
          {clientDevis.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <DescriptionOutlined sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Aucun devis pour ce client
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Numéro</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Projet</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date d'émission</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Montant TTC
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientDevis
                    .sort((a, b) => new Date(b.dateEmission).getTime() - new Date(a.dateEmission).getTime())
                    .map((devisItem) => {
                      const projet = projets.find((p) => p.id === devisItem.projetId);
                      return (
                        <TableRow
                          key={devisItem.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/devis/${devisItem.id}`)}
                        >
                          <TableCell>{devisItem.numero}</TableCell>
                          <TableCell>
                            {projet ? (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {projet.titre}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{new Date(devisItem.dateEmission).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell>
                            <Chip
                              label={getStatutDevisLabel(devisItem.statut)}
                              color={getStatutDevisColor(devisItem.statut)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {devisItem.totalTTC.toFixed(2)} €
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/devis/${devisItem.id}`);
                              }}
                            >
                              Voir
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab Factures */}
        <TabPanel value={tabValue} index={2}>
          {clientFactures.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptLongOutlined sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Aucune facture pour ce client
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Numéro</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Projet</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date d'émission</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Échéance</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Montant TTC
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientFactures
                    .sort((a, b) => new Date(b.dateEmission).getTime() - new Date(a.dateEmission).getTime())
                    .map((factureItem) => {
                      const projet = projets.find((p) => p.id === factureItem.projetId);
                      return (
                        <TableRow
                          key={factureItem.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/factures/${factureItem.id}`)}
                        >
                          <TableCell>{factureItem.numero}</TableCell>
                          <TableCell>
                            {projet ? (
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {projet.titre}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={factureItem.type} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>{new Date(factureItem.dateEmission).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell>{new Date(factureItem.dateEcheance).toLocaleDateString('fr-FR')}</TableCell>
                          <TableCell>
                            <Chip
                              label={getStatutFactureLabel(factureItem.statut)}
                              color={getStatutFactureColor(factureItem.statut)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {(factureItem.montantTTC || 0).toFixed(2)} €
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/factures/${factureItem.id}`);
                              }}
                            >
                              Voir
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Paper>

      {/* Project Creation Dialog */}
      <Dialog
        open={projectDialogOpen}
        onClose={() => setProjectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Nouveau projet pour {client.prenom} {client.nom}</Typography>
            <IconButton onClick={() => setProjectDialogOpen(false)} size="small">
              <CloseOutlined />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box
            component="form"
            onSubmit={async (e: React.FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const { addProjet } = useProjetStore.getState();

              try {
                // Construire l'adresse complète
                const adresseComplete = projectCodePostal && projectVille
                  ? `${projectAddress}, ${projectCodePostal} ${projectVille}`
                  : projectAddress;

                await addProjet({
                  titre: formData.get('titre') as string,
                  description: formData.get('description') as string || undefined,
                  adresseChantier: adresseComplete,
                  clientId: client.id,
                  statut: StatutProjet.EN_ATTENTE_DEVIS,
                  dateDebut: formData.get('dateDebut') as string || undefined,
                  dateFin: formData.get('dateFin') as string || undefined,
                });
                await loadProjets();
                setProjectDialogOpen(false);
                // Reset all address fields
                setProjectAddress('');
                setProjectCodePostal('');
                setProjectVille('');
              } catch (error) {
                console.error('Erreur lors de la création du projet:', error);
                alert('Erreur lors de la création du projet');
              }
            }}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              name="titre"
              label="Titre du projet *"
              fullWidth
              required
              placeholder="Ex: Rénovation salle de bain"
            />

            <AddressAutocomplete
              value={projectAddress}
              onChange={setProjectAddress}
              onAddressSelect={(address) => {
                setProjectAddress(address.adresse);
                setProjectCodePostal(address.codePostal);
                setProjectVille(address.ville);
              }}
              label="Adresse du chantier *"
              placeholder="Ex: 8 Boulevard du Port"
              required
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Code postal *"
                fullWidth
                required
                value={projectCodePostal}
                onChange={(e) => setProjectCodePostal(e.target.value)}
              />

              <TextField
                label="Ville *"
                fullWidth
                required
                value={projectVille}
                onChange={(e) => setProjectVille(e.target.value)}
              />
            </Box>

            <TextField
              name="description"
              label="Description (optionnel)"
              fullWidth
              multiline
              rows={3}
              placeholder="Description du projet..."
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="dateDebut"
                label="Date début (optionnel)"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                name="dateFin"
                label="Date fin (optionnel)"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button onClick={() => {
                setProjectDialogOpen(false);
                setProjectAddress('');
                setProjectCodePostal('');
                setProjectVille('');
              }}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!projectAddress || !projectCodePostal || !projectVille}
              >
                Créer le projet
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ClientDetailPage;
